import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from '../primitives/cx.js';
import { Icon } from '../primitives/Icon.tsx';
import { Skeleton } from '../primitives/Skeleton.tsx';
import { TABLE_TO_CARDS_BREAKPOINT, control, pxValue } from '../tokens.js';
import { visibleRange, totalHeight } from './windowing.js';
import { planColumns, type LayoutMode } from '@whinerp/contracts/layout';
import { useMediaQuery } from './useMediaQuery.js';

/**
 * The list.
 *
 * Sticky header, sticky first column, selectable rows, per-column sort and
 * menu, user-chosen density, and a card list instead of a table below the
 * table breakpoint. Cursor-paginated by its caller: this component never
 * counts anything itself, because `rows.length` is the current page and a
 * total taken from it is a plausible-looking wrong number.
 *
 * The table always sits inside a declared scroll container. At 1280x720 a
 * dense list either scrolls inside itself or pushes the page sideways and
 * truncates its own column headers, and "Comp..." / "Creat..." is the state
 * this exists to avoid.
 */

export type ColumnAlign = 'start' | 'end';

export interface Column<R> {
  key: string;
  header: string;
  /** Rendered cell. Values are already formatted by the one formatter. */
  cell: (row: R) => ReactNode;
  /**
   * Never dropped by Simple mode. The column rows are identified by.
   *
   * A list whose identifying column was budgeted away is not a simpler list;
   * it is a list of rows nobody can tell apart.
   */
  essential?: boolean;
  /** Shown only in Advanced, whatever the budget allows. */
  advancedOnly?: boolean;
  /** Sortable by this API field name. Absent means the column cannot sort. */
  sortField?: string;
  align?: ColumnAlign;
  /** Fixed width, from the spacing scale. */
  width?: string;
  /** Kept out of the card list on small screens. */
  hideOnCard?: boolean;
}

export type RowDensity = 'comfortable' | 'compact';

export interface DataTableProps<R> {
  rows: R[];
  columns: Column<R>[];
  /**
   * Simple or Advanced (F-014), which decides how many columns are shown.
   *
   * Required rather than defaulted on purpose: a list that forgets to pass it
   * would silently ignore the person's preference, and the whole defect this
   * feature had was a preference nothing read. Making it required means the
   * compiler asks every list to answer.
   */
  mode: LayoutMode;
  rowId: (row: R) => string;
  /** What a row leads to. Rendering it as a link is what makes it keyboard-reachable. */
  rowHref?: (row: R) => string;
  onRowClick?: (row: R) => void;
  density?: RowDensity;
  sort?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortChange?: (field: string) => void;
  selection?: {
    selected: ReadonlySet<string>;
    onToggle: (id: string) => void;
    onToggleAll: () => void;
    /** Label for the select-all control, naming what it selects. */
    allLabel: string;
  };
  loading?: boolean;
  /** Rendered instead of rows when there are none. */
  empty?: ReactNode;
  /** Announced to assistive technology, and used for the table's own label. */
  label: string;
  /**
   * Strings this component renders. Passed in for the same reason ListChrome
   * takes its own: the component library has no message catalogue, so a string
   * written here would be English in every workspace.
   */
  labels: {
    sortBy: (column: string) => string;
    selectRow: (row: string) => string;
    rowsShown: (count: number) => string;
  };
  /** Rendered under the rows: the pagination footer. */
  footer?: ReactNode;
  /**
   * Group headings between the rows (F-021).
   *
   * The rows must already arrive in group order - this component inserts a
   * heading where the value changes and does not reorder anything. Regrouping
   * here would group one PAGE, and a group split across a page boundary shows
   * the same heading twice with no way to tell that is what happened.
   */
  group?: {
    /** The heading for a row, or null when the row has no value for it. */
    label: (row: R) => string | null;
    /** What the heading says for the rows with no value. Never a blank bar. */
    emptyLabel: string;
  };
}

export function DataTable<R>({
  rows,
  columns: declared,
  mode,
  rowId,
  rowHref,
  onRowClick,
  density = 'comfortable',
  sort,
  onSortChange,
  selection,
  loading = false,
  empty,
  label,
  labels,
  footer,
  group,
}: DataTableProps<R>): JSX.Element {
  /*
   * Simple mode drops columns here rather than in each list screen.
   *
   * One rule about what Simple means, applied in the one component every list
   * renders through. Curating a set per screen is a set per screen that
   * drifts, and the drift reads as one list obeying the preference and the
   * next ignoring it.
   */
  const { columns } = planColumns(declared, mode);

  // Below this, a table becomes a card list. The breakpoint is the token, so
  // the query and the stylesheet cannot drift apart.
  const isWide = useMediaQuery(`(min-width: ${TABLE_TO_CARDS_BREAKPOINT})`);
  const scroller = useRef<HTMLDivElement>(null);
  const [geometry, setGeometry] = useState({ scrollTop: 0, viewportHeight: 0 });

  const rowHeight = pxValue(
    density === 'compact' ? control.table.rowCompact : control.table.rowComfortable,
  );

  const measure = useCallback(() => {
    const element = scroller.current;
    if (!element) return;
    setGeometry({ scrollTop: element.scrollTop, viewportHeight: element.clientHeight });
  }, []);

  useEffect(() => {
    measure();
    const element = scroller.current;
    if (!element) return;
    element.addEventListener('scroll', measure, { passive: true });
    return () => element.removeEventListener('scroll', measure);
  }, [measure, rows.length]);

  /*
   * A grouped list renders whole, rather than through the window.
   *
   * The windowing arithmetic is uniform row heights - `paddingTop` is a count
   * of rows times one height - and a heading is not a row of that height. The
   * page is at most MAX_PAGE_SIZE rows, which is a size a browser renders
   * without help; the alternative is a scrollbar that lies by exactly the
   * height of every heading above the viewport.
   */
  const window = visibleRange({
    scrollTop: geometry.scrollTop,
    viewportHeight: geometry.viewportHeight,
    rowHeight,
    rowCount: rows.length,
    ...(group ? { viewportHeight: 0 } : {}),
  });
  const rendered = rows.slice(window.start, window.end);
  const columnCount = columns.length + (selection ? 1 : 0);

  if (loading) {
    return (
      <div data-component="DataTable" data-state="loading" className="flex flex-col gap-px">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className={density === 'compact' ? 'h-row-compact' : 'h-row-comfortable'} />
        ))}
      </div>
    );
  }

  if (rows.length === 0 && empty) {
    return (
      <div data-component="DataTable" data-state="empty">
        {empty}
      </div>
    );
  }

  if (!isWide) {
    return (
      <div data-component="DataTable" data-state="cards" className="flex flex-col gap-2">
        {rows.map((row, index) => (
          <Fragment key={rowId(row)}>
            {group && startsGroup(rows, index, group.label) ? (
              <h3
                data-group-heading={group.label(row) ?? ''}
                className="px-1 pt-2 text-micro uppercase tracking-label text-ink-label"
              >
                {group.label(row) ?? group.emptyLabel}
              </h3>
            ) : null}
            <CardRow
              row={row}
              columns={columns.filter((column) => !column.hideOnCard)}
              href={rowHref?.(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            />
          </Fragment>
        ))}
        {footer}
      </div>
    );
  }

  return (
    <div data-component="DataTable" className="flex min-h-0 flex-col">
      {/*
        The scroll container. Declared with an attribute as well as a class so
        check:responsive can find it without a layout engine: the rule is that
        a table is always inside one, and a rule enforced by inspection is a
        rule that holds.
      */}
      <div
        ref={scroller}
        data-scroll-container="x"
        className="relative min-h-0 flex-1 overflow-auto rounded-card bg-surface"
      >
        <table className="w-full border-collapse text-dense" aria-label={label}>
          <thead className="sticky top-0 z-10">
            <tr className="h-table-header bg-sunken text-start">
              {selection ? (
                <th scope="col" className="sticky start-0 z-20 w-touch bg-sunken px-3">
                  <input
                    type="checkbox"
                    // A stable hook: the select-all box has to be findable
                    // whatever language its label is in.
                    data-select-all
                    aria-label={selection.allLabel}
                    checked={rows.length > 0 && rows.every((row) => selection.selected.has(rowId(row)))}
                    onChange={selection.onToggleAll}
                    className="h-icon-inline w-icon-inline rounded-control border-control"
                  />
                </th>
              ) : null}
              {columns.map((column, index) => (
                <th
                  key={column.key}
                  scope="col"
                  // Named, like the cells below it. A header addressed by
                  // position breaks when a selection checkbox appears, and
                  // addressed by its text it breaks in the other language.
                  data-column={column.key}
                  style={column.width ? { width: column.width } : undefined}
                  className={cx(
                    'whitespace-nowrap px-3 text-micro uppercase tracking-label text-ink-label',
                    column.align === 'end' ? 'text-end' : 'text-start',
                    // The first column stays put while the rest scroll sideways.
                    index === 0 && !selection ? 'sticky start-0 z-20 bg-sunken' : '',
                  )}
                >
                  {column.sortField && onSortChange ? (
                    <button
                      type="button"
                      onClick={() => onSortChange(column.sortField ?? column.key)}
                      aria-label={labels.sortBy(column.header)}
                      className="inline-flex h-table-header items-center gap-1 text-micro uppercase tracking-label text-ink-label hover:text-ink"
                    >
                      {column.header}
                      {sort?.field === column.sortField ? (
                        <Icon
                          name={sort.direction === 'asc' ? 'ArrowUp' : 'ArrowDown'}
                          size="inline"
                          decorative
                        />
                      ) : null}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {window.paddingTop > 0 ? (
              <tr aria-hidden style={{ height: window.paddingTop }}>
                <td colSpan={columns.length + (selection ? 1 : 0)} />
              </tr>
            ) : null}

            {rendered.map((row, offset) => {
              const id = rowId(row);
              const index = window.start + offset;
              const selected = selection?.selected.has(id) ?? false;
              const heading =
                group && startsGroup(rows, index, group.label)
                  ? (group.label(row) ?? group.emptyLabel)
                  : null;
              return (
                <Fragment key={id}>
                {heading !== null ? (
                  <tr className="border-t border-t-rule bg-sunken">
                    <th
                      scope="colgroup"
                      colSpan={columnCount}
                      data-group-heading={group?.label(row) ?? ''}
                      className="px-3 py-1.5 text-start text-micro uppercase tracking-label text-ink-label"
                    >
                      {heading}
                    </th>
                  </tr>
                ) : null}
                <tr
                  data-selected={selected || undefined}
                  className={cx(
                    'border-t border-t-rule',
                    density === 'compact' ? 'h-row-compact' : 'h-row-comfortable',
                    selected ? 'bg-bronze-tint' : 'bg-surface hover:bg-sunken',
                  )}
                >
                  {selection ? (
                    <td className="sticky start-0 z-10 w-touch bg-inherit px-3">
                      <input
                        type="checkbox"
                        data-select-row
                        aria-label={labels.selectRow(id)}
                        checked={selected}
                        onChange={() => selection.onToggle(id)}
                        className="h-icon-inline w-icon-inline rounded-control border-control"
                      />
                    </td>
                  ) : null}
                  {columns.map((column, index) => (
                    <td
                      key={column.key}
                      // Named, so a test can address the column it means rather
                      // than counting cells past a selection checkbox that may
                      // or may not be there.
                      data-column={column.key}
                      className={cx(
                        'truncate px-3 text-dense text-ink',
                        column.align === 'end' ? 'text-end' : 'text-start',
                        index === 0 && !selection ? 'sticky start-0 z-10 bg-inherit' : '',
                      )}
                    >
                      {index === 0 && rowHref ? (
                        <a href={rowHref(row)} className="font-medium text-ink hover:text-bronze-text">
                          {column.cell(row)}
                        </a>
                      ) : (
                        column.cell(row)
                      )}
                    </td>
                  ))}
                </tr>
                </Fragment>
              );
            })}

            {window.paddingBottom > 0 ? (
              <tr aria-hidden style={{ height: window.paddingBottom }}>
                <td colSpan={columns.length + (selection ? 1 : 0)} />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {footer}
      <span className="sr-only" role="status">
        {labels.rowsShown(rows.length)}
      </span>
      <span hidden data-total-height={totalHeight(rows.length, rowHeight)} />
    </div>
  );
}

function CardRow<R>({
  row,
  columns,
  href,
  onClick,
}: {
  row: R;
  columns: Column<R>[];
  href?: string;
  onClick?: () => void;
}): JSX.Element {
  const [first, ...rest] = columns;
  const body = (
    <>
      <span className="text-body font-medium text-ink">{first?.cell(row)}</span>
      <span className="flex flex-wrap gap-3 text-meta text-ink-meta">
        {rest.slice(0, 3).map((column) => (
          <span key={column.key}>{column.cell(row)}</span>
        ))}
      </span>
    </>
  );
  return href ? (
    <a
      href={href}
      data-component="CardRow"
      className="flex min-h-touch flex-col justify-center gap-1 rounded-card bg-surface p-3"
    >
      {body}
    </a>
  ) : (
    <button
      type="button"
      onClick={onClick}
      data-component="CardRow"
      className="flex min-h-touch flex-col justify-center gap-1 rounded-card bg-surface p-3 text-start"
    >
      {body}
    </button>
  );
}

/**
 * Whether a row starts a new group.
 *
 * Compared against the row before it in the WHOLE page, not in the rendered
 * window: a window that begins mid-group would otherwise print the heading
 * again the moment somebody scrolled.
 */
function startsGroup<R>(rows: R[], index: number, label: (row: R) => string | null): boolean {
  const row = rows[index];
  if (row === undefined) return false;
  if (index === 0) return true;
  const previous = rows[index - 1];
  return previous === undefined || label(previous) !== label(row);
}
